<script lang="ts">
  import { link } from 'svelte-spa-router';
  import { auth } from '../../js/auth.js';
  import { teamBilling } from '../../js/teamBillingStore.js';
  import {
    includedPlan,
    remainingPack,
    remainingPlan,
  } from '../../js/teamBillingCache.js';

  /** `inline` = top-right pill; `sidebar` = full-width under user card */
  export let layout: 'inline' | 'sidebar' = 'inline';

  $: teamUuid = $auth.user?.active_team_uuid ?? null;
  $: billing = $teamBilling;
  $: usageLoading = billing.loading;
  $: teamUsage = billing.usage;

  $: planLabel = (() => {
    if (!teamUuid) return 'No team';
    if (!teamUsage) return usageLoading ? '' : 'No plan';
    if (teamUsage.is_trial) return 'Free trial';
    if (teamUsage.plan_name) return teamUsage.plan_name;
    return 'No plan';
  })();

  $: planCreditsLabel = (() => {
    if (!teamUuid || !teamUsage) return usageLoading ? '' : '—';
    if (teamUsage.is_trial) return 'Unlimited';
    const included = includedPlan(teamUsage);
    if (included === -1) return 'Unlimited';
    if (included === 0) return 'Pack only';
    return `${remainingPlan(teamUsage)} / ${included}`;
  })();

  $: extraCreditsLabel = teamUsage
    ? String(remainingPack(teamUsage))
    : usageLoading ? '' : '0';
</script>

{#if teamUuid}
  <a
    use:link
    href="/payment-plan"
    class="team-billing-strip"
    class:layout-sidebar={layout === 'sidebar'}
    title="View plan and billing"
  >
    <span class="strip-segment strip-plan">
      <span class="strip-label">Plan</span>
      {#if usageLoading && !planLabel}
        <span class="strip-skeleton" aria-hidden="true"></span>
      {:else}
        <span class="strip-value">{planLabel}</span>
      {/if}
    </span>
    <span class="strip-divider" aria-hidden="true"></span>
    <span class="strip-segment">
      <span class="strip-label">Credits</span>
      {#if usageLoading && !planCreditsLabel}
        <span class="strip-skeleton" aria-hidden="true"></span>
      {:else}
        <span class="strip-value">{planCreditsLabel}</span>
      {/if}
    </span>
    <span class="strip-divider" aria-hidden="true"></span>
    <span class="strip-segment">
      <span class="strip-label">Extra</span>
      {#if usageLoading && !extraCreditsLabel}
        <span class="strip-skeleton" aria-hidden="true"></span>
      {:else}
        <span class="strip-value">{extraCreditsLabel}</span>
      {/if}
    </span>
  </a>
{/if}

<style>
  .team-billing-strip {
    display: inline-flex;
    align-items: stretch;
    flex-wrap: wrap;
    gap: 0;
    padding: var(--space-2) var(--space-3);
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-card);
    text-decoration: none;
    color: inherit;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .team-billing-strip:hover {
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .team-billing-strip:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .team-billing-strip.layout-sidebar {
    display: flex;
    width: 100%;
    justify-content: space-between;
    box-sizing: border-box;
    min-height: var(--sidebar-panel-height, 4.25rem);
    align-items: center;
  }

  .layout-sidebar .strip-segment {
    flex: 1;
    min-width: 0;
    padding: 0 var(--space-2);
    align-items: flex-start;
  }

  .layout-sidebar .strip-segment:first-child {
    padding-left: 0;
  }

  .layout-sidebar .strip-segment:last-child {
    padding-right: 0;
  }

  .layout-sidebar .strip-value {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .strip-segment {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 0 var(--space-3);
    min-width: 4.5rem;
  }

  .strip-segment:first-child {
    padding-left: 0;
  }

  .strip-segment:last-child {
    padding-right: 0;
  }

  .strip-plan .strip-value {
    color: var(--primary);
  }

  .strip-label {
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--grey-secondary);
    line-height: 1.2;
  }

  .strip-value {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    line-height: 1.25;
    min-height: 1.25rem;
  }

  .strip-divider {
    width: 1px;
    align-self: stretch;
    background: var(--border-color);
    flex-shrink: 0;
    margin: 2px 0;
  }

  .strip-skeleton {
    display: inline-block;
    height: 0.875rem;
    width: 3.5rem;
    border-radius: 4px;
    background: linear-gradient(90deg, var(--greyish) 25%, var(--surface) 50%, var(--greyish) 75%);
    background-size: 200% 100%;
    animation: strip-shimmer 1.5s infinite;
  }

  @keyframes strip-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @media (max-width: 768px) {
    .team-billing-strip {
      width: 100%;
      justify-content: space-between;
    }

    .strip-segment {
      flex: 1;
      min-width: 0;
      padding: 0 var(--space-2);
    }
  }
</style>
